import React, { useState, useEffect } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Grid, MetricCard } from '../components/UI';
import { supabase } from '../supabaseClient';
const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function useIsMobile() {
  const [m,setM]=React.useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  React.useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return m;
}
function LocationQualityBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ col:'score', dir:'desc' });

  useEffect(() => {
    (async () => {
      try {
      const { data: rows } = await supabase
        .from('Locations')
        .select('country,latitude,phone,website,booking_url,last_verified,name,type')
        .limit(10000);
      const map = {};
      const d365 = new Date(Date.now()-365*24*60*60*1000).toISOString().slice(0,10);
      (rows||[]).forEach(r => {
        const c = r.country||'Unknown';
        if (!map[c]) map[c]={country:c,total:0,coords:0,phone:0,website:0,booking:0,verified:0};
        map[c].total++;
        if (r.latitude)  map[c].coords++;
        if (r.phone)     map[c].phone++;
        if (r.website)   map[c].website++;
        if (r.booking_url) map[c].booking++;
        if (r.last_verified && r.last_verified >= d365) map[c].verified++;
      });
      const p = (n,t) => t>0?Math.round(n/t*100):0;
      const result = Object.values(map).map(r => {
        const coordP  = p(r.coords,r.total);
        const phoneP  = p(r.phone,r.total);
        const webP    = p(r.website,r.total);
        const bookP   = p(r.booking,r.total);
        const verP    = p(r.verified,r.total);
        // Weighted quality score: coords 30%, phone 20%, website 20%, booking 15%, verified 15%
        const score   = Math.round(coordP*.30 + phoneP*.20 + webP*.20 + bookP*.15 + verP*.15);
        return {...r,coordP,phoneP,webP,bookP,verP,score};
      });
      // Overall totals
      const tot = {total:0,coords:0,phone:0,website:0,booking:0,verified:0};
      result.forEach(r=>{Object.keys(tot).forEach(k=>{tot[k]+=(r[k]||0);})});
      setTotals({...tot,
        coordP:p(tot.coords,tot.total),phoneP:p(tot.phone,tot.total),
        webP:p(tot.website,tot.total),bookP:p(tot.booking,tot.total),verP:p(tot.verified,tot.total),
        score:Math.round(p(tot.coords,tot.total)*.30+p(tot.phone,tot.total)*.20+p(tot.website,tot.total)*.20+p(tot.booking,tot.total)*.15+p(tot.verified,tot.total)*.15)
      });
      setData(result);
      } catch (_e) {} finally { setLoading(false); }
    })();
  }, []);

  const sorted = [...data].sort((a,b) => {
    const av=a[sort.col]??0, bv=b[sort.col]??0;
    return sort.dir==='desc'?bv-av:av-bv;
  });

  const bar = (p,maxW=48) => {
    const c = p>=80?'#28a745':p>=50?'#fd7e14':'#dc3545';
    return <div style={{display:'flex',alignItems:'center',gap:4}}>
      <div style={{width:maxW,height:4,borderRadius:2,background:'var(--color-background-tertiary)',overflow:'hidden'}}>
        <div style={{height:'100%',width:p+'%',background:c,borderRadius:2}}/>
      </div>
      <span style={{fontSize:'.65rem',color:c,minWidth:28}}>{p}%</span>
    </div>;
  };

  const th = (k,l) => <th onClick={()=>setSort(s=>({col:k,dir:s.col===k&&s.dir==='desc'?'asc':'desc'}))}
    style={{fontSize:'.63rem',color:sort.col===k?'#314F5C':'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.04em',padding:'6px 8px',textAlign:'left',borderBottom:'1px solid var(--color-border-tertiary)',fontWeight:500,cursor:'pointer',whiteSpace:'nowrap'}}>
    {l}{sort.col===k?(sort.dir==='desc'?' ↓':' ↑'):' ↕'}
  </th>;
  const td = {fontSize:'.72rem',padding:'6px 8px',borderBottom:'.5px solid var(--color-border-tertiary)',color:'var(--color-text-primary)'};

  return (
    <div style={{fontFamily:F,padding:isMobile?'16px 14px 80px':'24px 28px'}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:500,margin:0,color:'var(--color-text-primary)'}}>Location quality score</h2>
        <p style={{fontSize:'.78rem',color:'var(--color-text-secondary)',margin:'4px 0 0'}}>
          Composite score per country: GPS (30%) + phone (20%) + website (20%) + booking URL (15%) + verified within 1yr (15%).
        </p>
      </div>
      {totals&&(
        <Grid cols={4} style={{marginBottom:20}}>
          <MetricCard label="Overall quality score" value={loading?'…':totals.score+'%'} accent={totals.score>=70?'#28a745':totals.score>=50?'#fd7e14':'#dc3545'} />
          <MetricCard label="GPS coverage" value={loading?'…':totals.coordP+'%'} sub="coordinates filled" accent="#314F5C" />
          <MetricCard label="Phone coverage" value={loading?'…':totals.phoneP+'%'} sub="phone numbers filled" accent="#6F8FA7" />
          <MetricCard label="Website coverage" value={loading?'…':totals.webP+'%'} sub="websites filled" accent="#819D84" />
        </Grid>
      )}
      <Card>
        {loading?<div style={{padding:20,color:'var(--color-text-secondary)',fontSize:'.78rem'}}>Loading…</div>:(
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
              <thead><tr>
                {th('country','Country')}{th('total','Locations')}{th('score','Score')}{th('coordP','GPS')}{th('phoneP','Phone')}{th('webP','Website')}{th('bookP','Booking')}{th('verP','Verified')}
              </tr></thead>
              <tbody>{sorted.slice(0,40).map(r=>(
                <tr key={r.country}>
                  <td style={{...td,fontWeight:500}}>{r.country}</td>
                  <td style={td}>{r.total.toLocaleString()}</td>
                  <td style={{...td}}><span style={{fontWeight:700,color:r.score>=70?'#28a745':r.score>=50?'#fd7e14':'#dc3545'}}>{r.score}%</span></td>
                  <td style={td}>{bar(r.coordP)}</td>
                  <td style={td}>{bar(r.phoneP)}</td>
                  <td style={td}>{bar(r.webP)}</td>
                  <td style={td}>{bar(r.bookP)}</td>
                  <td style={td}>{bar(r.verP)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function LocationQuality(props) {
  if (props && props.embed) return <LocationQualityBody {...props} />;
  return <ConsoleShell title="Location Quality"><LocationQualityBody {...props} /></ConsoleShell>;
}
